/**
 * Pane themes — the per-persona theme tokens that drive the standardized
 * brand pages. Every Aspect (sidebar, topbar, program card, stat tile, etc.)
 * is rendered identically across panes; only these tokens change. Clicking
 * between panes should reveal the brand's minute differences.
 */
import { COLORS, FONTS } from "../../lib/brand";
import type { PaneKey } from "../../lib/brand-routes";

export type PaneTheme = {
  paneId: PaneKey;

  // Surfaces
  pageBg: string;
  contentBg: string;
  surfaceBg: string;
  surfaceRaisedBg: string;
  sidebarBg: string;
  sidebarItemActiveBg: string;
  topbarBg: string;

  // Text
  textOnSurface: string;
  textOnSurfaceDim: string;
  textOnSurfaceFaint: string;
  textOnSidebar: string;
  textOnSidebarDim: string;
  textOnTopbar: string;
  textOnTopbarDim: string;

  // Borders + rules
  border: string;
  borderStrong: string;
  divider: string;

  // Extended dark-surface tints — added 2026-05-27 to close
  // Student shell gaps. Used by sidebar/topbar chrome and
  // dark dividers.
  accentTintSoft: string;
  neutralHoverBgOnDark: string;
  surfaceDividerFaint: string;
  surfaceDividerStrong: string;
  textOnSidebarFaint: string;
  textOnSidebarIdle: string;

  // Status-chip + hover tints — added 2026-05-28 for Status Card aspect.
  successTintSoft: string;
  successBorderSoft: string;
  accentBorderSoft: string;
  accentBorderHover: string;

  // Soft amber glow ring around active markers. Between
  // accentTintSoft (faint backdrop) and sidebarItemActiveBg
  // (filled active state). Added 2026-05-28 for Timeline Rail.
  accentHalo: string;

  // Alternate / editorial archetype — the SECOND surface treatment this
  // pane uses. For General/Student this is the SAND/OFFW light band; for
  // Client/Admin this is the FOREST_MID callout panel surface. Brand page
  // shows it so reviewers see every archetype this pane actually ships.
  altBg: string;
  altTextOnSurface: string;
  altTextOnSurfaceDim: string;
  altBorder: string;
  altSurfaceLabel: string;

  // Accent
  accent: string;
  accentText: string;        // text color on accent fill
  accentSoft: string;        // soft tint for backgrounds
  accentDeep: string;
  destructive: string;
  success: string;

  hasSidebar: boolean;

  // Type roles
  displayFont: string;
  displayItalicFont: string;
  displayMediumFont: string;
  bodyFont: string;
  bodyMediumFont: string;
  bodySemiBoldFont: string;
  bodyBoldFont: string;

  // Voice character name only. All structural copy lives in StandardPane
  // as constants so the aspects panel reads as a pure structural reference
  // (no marketing copy distractions).
  voice: {
    label: string; // "Editorial", "Direct-Product", etc.
  };

  // Program card variant for this surface
  cardStyle: {
    bg: string;
    borderColor: string;
    borderLeftAccent: boolean;
    textOnCard: string;
    textDimOnCard: string;
    tagOnCard: string;
  };

  // Brand atoms — standardized slot maps. Every pane defines a value for
  // each slot; slot KEYS are identical across panes so the footer rows up
  // 1:1 when toggling between panes.
  atoms: {
    colors: Record<ColorSlot, keyof typeof COLORS>;
    style: Record<StyleSlot, string>;
  };
};

export const COLOR_SLOTS = [
  "Initial Background",
  "Sidebar",
  "Topbar",
  "Card Surface",
  "Accent Band",
  "Accent",
  "Status / Live",
  "Destructive",
] as const;
export type ColorSlot = (typeof COLOR_SLOTS)[number];

export const STYLE_SLOTS = ["Thesis", "Simplicity", "Flow", "Motion"] as const;
export type StyleSlot = (typeof STYLE_SLOTS)[number];

/* ────────────────────────────────────────────────────────────
 * General — marketing surfaces. Dark forest, Cormorant
 * headlines, amber accent, editorial voice. No sidebar.
 * ──────────────────────────────────────────────────────────── */

const GENERAL: PaneTheme = {
  paneId: "General",
  pageBg: COLORS.FOREST_GENERAL.hex,
  contentBg: COLORS.FOREST_GENERAL.hex,
  surfaceBg: "rgba(247,244,238,0.03)",
  surfaceRaisedBg: COLORS.FOREST_MID.hex,
  sidebarBg: COLORS.FOREST_GENERAL.hex,
  sidebarItemActiveBg: "rgba(181,135,58,0.14)",
  topbarBg: COLORS.FOREST_GENERAL.hex,
  textOnSurface: COLORS.LINEN.hex,
  textOnSurfaceDim: "rgba(247,244,238,0.66)",
  textOnSurfaceFaint: "rgba(247,244,238,0.34)",
  textOnSidebar: COLORS.LINEN.hex,
  textOnSidebarDim: "rgba(247,244,238,0.55)",
  textOnTopbar: COLORS.LINEN.hex,
  textOnTopbarDim: "rgba(247,244,238,0.55)",
  border: "rgba(181,135,58,0.16)",
  borderStrong: "rgba(181,135,58,0.40)",
  divider: "rgba(247,244,238,0.08)",
  altBg: COLORS.SAND_GENERAL.hex,
  altTextOnSurface: COLORS.FOREST.hex,
  altTextOnSurfaceDim: "rgba(28,43,26,0.62)",
  altBorder: "rgba(28,43,26,0.10)",
  altSurfaceLabel: "Editorial · Sand archetype",
  accent: COLORS.AMBER.hex,
  accentText: "#0E0E0E",
  accentSoft: "rgba(181,135,58,0.14)",
  accentDeep: COLORS.AMBER_DEEP.hex,
  destructive: COLORS.RED_ERROR.hex,
  success: COLORS.LIVE_GREEN.hex,
  hasSidebar: true,
  displayFont: FONTS.displayLight,
  displayItalicFont: FONTS.displayItalic,
  displayMediumFont: FONTS.displayMedium,
  bodyFont: FONTS.body,
  bodyMediumFont: FONTS.bodyMedium,
  bodySemiBoldFont: FONTS.bodySemiBold,
  bodyBoldFont: FONTS.bodyBold,
  voice: { label: "Editorial" },
  cardStyle: {
    bg: COLORS.FOREST_MID.hex,
    borderColor: "rgba(181,135,58,0.22)",
    borderLeftAccent: true,
    textOnCard: COLORS.LINEN.hex,
    textDimOnCard: "rgba(247,244,238,0.55)",
    tagOnCard: COLORS.AMBER.hex,
  },
  atoms: {
    colors: {
      "Initial Background": "FOREST_GENERAL",
      "Sidebar": "FOREST_GENERAL",
      "Topbar": "FOREST_GENERAL",
      "Card Surface": "FOREST_MID",
      "Accent Band": "SAND_GENERAL",
      "Accent": "AMBER",
      "Status / Live": "LIVE_GREEN",
      "Destructive": "RED_ERROR",
    },
    style: {
      "Thesis": "Let the brand breathe. Marketing is the only place we shout.",
      "Simplicity": "Sparse layouts. White space lifts the headline; bullets stay short.",
      "Flow": "Read top to bottom. Two CTAs land on the right rail — filled + outlined.",
      "Motion": "Restrained. Subtle hover lifts on links, no auto-playing motion.",
    },
  },
  accentTintSoft: "rgba(181,135,58,0.10)",
  neutralHoverBgOnDark: "rgba(255,255,255,0.04)",
  surfaceDividerFaint: "rgba(247,244,238,0.08)",
  surfaceDividerStrong: "rgba(247,244,238,0.18)",
  textOnSidebarFaint: "rgba(247,244,238,0.30)",
  textOnSidebarIdle: "rgba(247,244,238,0.48)",

  successTintSoft: "rgba(100,127,77,0.10)",
  successBorderSoft: "rgba(100,127,77,0.28)",
  accentBorderSoft: "rgba(181,135,58,0.30)",
  accentBorderHover: "rgba(181,135,58,0.45)",

  accentHalo: "rgba(181,135,58,0.18)",

};

/* ────────────────────────────────────────────────────────────
 * Student — light editorial portal. FOREST topbar, LINEN
 * content surfaces, AMBER accent. Warmer voice.
 * ──────────────────────────────────────────────────────────── */

const STUDENT: PaneTheme = {
  paneId: "Student",
  pageBg: COLORS.SAND.hex,
  contentBg: COLORS.SAND.hex,
  surfaceBg: COLORS.LINEN.hex,
  surfaceRaisedBg: "#FFFFFF",
  sidebarBg: COLORS.FOREST.hex,
  sidebarItemActiveBg: "rgba(181,135,58,0.22)",
  topbarBg: COLORS.FOREST.hex,
  textOnSurface: COLORS.FOREST.hex,
  textOnSurfaceDim: "rgba(28,43,26,0.62)",
  textOnSurfaceFaint: "rgba(28,43,26,0.36)",
  textOnSidebar: COLORS.LINEN.hex,
  textOnSidebarDim: "rgba(247,244,238,0.55)",
  textOnTopbar: COLORS.LINEN.hex,
  textOnTopbarDim: "rgba(247,244,238,0.55)",
  border: "rgba(28,43,26,0.10)",
  borderStrong: "rgba(28,43,26,0.22)",
  divider: "rgba(28,43,26,0.08)",
  altBg: COLORS.OFFW.hex,
  altTextOnSurface: COLORS.FOREST.hex,
  altTextOnSurfaceDim: "rgba(28,43,26,0.62)",
  altBorder: "rgba(28,43,26,0.12)",
  altSurfaceLabel: "Editorial hero band",
  accent: COLORS.AMBER.hex,
  accentText: "#0E0E0E",
  accentSoft: "rgba(181,135,58,0.12)",
  accentDeep: COLORS.AMBER_DEEP.hex,
  destructive: "#9C3A2C",
  success: COLORS.SAGE_DEEP.hex,
  hasSidebar: true,
  displayFont: FONTS.displayMedium,
  displayItalicFont: FONTS.displayItalic,
  displayMediumFont: FONTS.displayMedium,
  bodyFont: FONTS.body,
  bodyMediumFont: FONTS.bodyMedium,
  bodySemiBoldFont: FONTS.bodySemiBold,
  bodyBoldFont: FONTS.bodyBold,
  voice: { label: "Editorial-Warm" },
  cardStyle: {
    bg: "#FFFFFF",
    borderColor: "rgba(28,43,26,0.10)",
    borderLeftAccent: true,
    textOnCard: COLORS.FOREST.hex,
    textDimOnCard: "rgba(28,43,26,0.62)",
    tagOnCard: COLORS.AMBER_DEEP.hex,
  },
  atoms: {
    colors: {
      "Initial Background": "SAND",
      "Sidebar": "FOREST",
      "Topbar": "FOREST",
      "Card Surface": "LINEN",
      "Accent Band": "OFFW",
      "Accent": "AMBER",
      "Status / Live": "SAGE_DEEP",
      "Destructive": "RED_ERROR",
    },
    style: {
      "Thesis": "Warm and quiet — the student's work leads, the chrome stays out of the way.",
      "Simplicity": "Comfortable density. One card per concept, generous padding, no clutter.",
      "Flow": "FOREST sidebar on wide, pill nav on narrow. One workspace per competition.",
      "Motion": "Soft. Drawers slide in, modals fade — no spring overshoot.",
    },
  },
  accentTintSoft: "rgba(181,135,58,0.10)",
  neutralHoverBgOnDark: "rgba(255,255,255,0.04)",
  surfaceDividerFaint: "rgba(247,244,238,0.08)",
  surfaceDividerStrong: "rgba(247,244,238,0.18)",
  textOnSidebarFaint: "rgba(247,244,238,0.30)",
  textOnSidebarIdle: "rgba(247,244,238,0.48)",

  successTintSoft: "rgba(100,127,77,0.10)",
  successBorderSoft: "rgba(100,127,77,0.28)",
  accentBorderSoft: "rgba(181,135,58,0.30)",
  accentBorderHover: "rgba(181,135,58,0.45)",

  accentHalo: "rgba(181,135,58,0.18)",

};

/* ────────────────────────────────────────────────────────────
 * Client — utilitarian dark portal. BLACK page bg with
 * FOREST sidebar, OFF_BLACK card surfaces, AMBER accent.
 * Voice is direct, product-led.
 * ──────────────────────────────────────────────────────────── */

const CLIENT: PaneTheme = {
  paneId: "Client",
  pageBg: COLORS.BLACK.hex,
  contentBg: COLORS.BLACK.hex,
  surfaceBg: COLORS.OFF_BLACK.hex,
  surfaceRaisedBg: "#1E1E1E",
  sidebarBg: COLORS.FOREST.hex,
  sidebarItemActiveBg: "rgba(181,135,58,0.22)",
  topbarBg: COLORS.BLACK.hex,
  textOnSurface: COLORS.LINEN.hex,
  textOnSurfaceDim: "rgba(247,244,238,0.66)",
  textOnSurfaceFaint: "rgba(247,244,238,0.34)",
  textOnSidebar: COLORS.LINEN.hex,
  textOnSidebarDim: "rgba(247,244,238,0.55)",
  textOnTopbar: COLORS.LINEN.hex,
  textOnTopbarDim: "rgba(247,244,238,0.55)",
  border: "rgba(247,244,238,0.08)",
  borderStrong: "rgba(181,135,58,0.40)",
  divider: "rgba(247,244,238,0.06)",
  altBg: COLORS.FOREST_DEEP.hex,
  altTextOnSurface: COLORS.LINEN.hex,
  altTextOnSurfaceDim: "rgba(247,244,238,0.62)",
  altBorder: "rgba(181,135,58,0.18)",
  altSurfaceLabel: "Dark callout panel",
  accent: COLORS.AMBER.hex,
  accentText: "#0E0E0E",
  accentSoft: "rgba(181,135,58,0.10)",
  accentDeep: COLORS.AMBER_DEEP.hex,
  destructive: COLORS.RED_ERROR.hex,
  success: COLORS.LIVE_GREEN.hex,
  hasSidebar: true,
  displayFont: FONTS.displayMedium,
  displayItalicFont: FONTS.displayItalic,
  displayMediumFont: FONTS.displayMedium,
  bodyFont: FONTS.body,
  bodyMediumFont: FONTS.bodyMedium,
  bodySemiBoldFont: FONTS.bodySemiBold,
  bodyBoldFont: FONTS.bodyBold,
  voice: { label: "Direct-Product" },
  cardStyle: {
    bg: COLORS.OFF_BLACK.hex,
    borderColor: "rgba(247,244,238,0.08)",
    borderLeftAccent: false,
    textOnCard: COLORS.LINEN.hex,
    textDimOnCard: "rgba(247,244,238,0.66)",
    tagOnCard: COLORS.AMBER.hex,
  },
  atoms: {
    colors: {
      "Initial Background": "BLACK",
      "Sidebar": "FOREST",
      "Topbar": "BLACK",
      "Card Surface": "OFF_BLACK",
      "Accent Band": "FOREST_DEEP",
      "Accent": "AMBER",
      "Status / Live": "LIVE_GREEN",
      "Destructive": "RED_ERROR",
    },
    style: {
      "Thesis": "Spin up programs, watch candidates fill in, shortlist fast. Stay out of the way.",
      "Simplicity": "Dense by design. Tables over tiles, filters always visible, no hero copy.",
      "Flow": "Sidebar nav, action-first dashboard. Every screen surfaces the next decision.",
      "Motion": "Minimal. No surprise transitions — state changes happen in place.",
    },
  },
  accentTintSoft: "rgba(181,135,58,0.10)",
  neutralHoverBgOnDark: "rgba(255,255,255,0.04)",
  surfaceDividerFaint: "rgba(247,244,238,0.08)",
  surfaceDividerStrong: "rgba(247,244,238,0.18)",
  textOnSidebarFaint: "rgba(247,244,238,0.30)",
  textOnSidebarIdle: "rgba(247,244,238,0.48)",

  successTintSoft: "rgba(100,127,77,0.10)",
  successBorderSoft: "rgba(100,127,77,0.28)",
  accentBorderSoft: "rgba(181,135,58,0.30)",
  accentBorderHover: "rgba(181,135,58,0.45)",

  accentHalo: "rgba(181,135,58,0.18)",

};

/* ────────────────────────────────────────────────────────────
 * Admin — internal staff portal. BLACK page bg, FOREST_DEEP
 * sidebar, RED for destructive. Plain technical voice.
 * ──────────────────────────────────────────────────────────── */

const ADMIN: PaneTheme = {
  paneId: "Admin",
  pageBg: COLORS.BLACK.hex,
  contentBg: COLORS.BLACK.hex,
  surfaceBg: COLORS.OFF_BLACK.hex,
  surfaceRaisedBg: COLORS.FOREST_MID.hex,
  sidebarBg: COLORS.FOREST.hex,
  sidebarItemActiveBg: "rgba(181,135,58,0.18)",
  topbarBg: COLORS.BLACK.hex,
  textOnSurface: COLORS.LINEN.hex,
  textOnSurfaceDim: "rgba(247,244,238,0.62)",
  textOnSurfaceFaint: "rgba(247,244,238,0.32)",
  textOnSidebar: COLORS.LINEN.hex,
  textOnSidebarDim: "rgba(247,244,238,0.50)",
  textOnTopbar: COLORS.LINEN.hex,
  textOnTopbarDim: "rgba(247,244,238,0.55)",
  border: "rgba(247,244,238,0.08)",
  borderStrong: "rgba(247,244,238,0.18)",
  divider: "rgba(247,244,238,0.06)",
  altBg: COLORS.FOREST_DEEPER.hex,
  altTextOnSurface: COLORS.LINEN.hex,
  altTextOnSurfaceDim: "rgba(247,244,238,0.55)",
  altBorder: "rgba(181,135,58,0.18)",
  altSurfaceLabel: "Audit log diff panel",
  accent: COLORS.AMBER.hex,
  accentText: "#0E0E0E",
  accentSoft: "rgba(181,135,58,0.10)",
  accentDeep: COLORS.AMBER_DEEP.hex,
  destructive: COLORS.RED_ERROR.hex,
  success: COLORS.LIVE_GREEN.hex,
  hasSidebar: true,
  displayFont: FONTS.displayMedium,
  displayItalicFont: FONTS.displayItalic,
  displayMediumFont: FONTS.displayMedium,
  bodyFont: FONTS.body,
  bodyMediumFont: FONTS.bodyMedium,
  bodySemiBoldFont: FONTS.bodySemiBold,
  bodyBoldFont: FONTS.bodyBold,
  voice: { label: "Plain-Technical" },
  cardStyle: {
    bg: COLORS.OFF_BLACK.hex,
    borderColor: "rgba(247,244,238,0.08)",
    borderLeftAccent: false,
    textOnCard: COLORS.LINEN.hex,
    textDimOnCard: "rgba(247,244,238,0.62)",
    tagOnCard: COLORS.AMBER.hex,
  },
  atoms: {
    colors: {
      "Initial Background": "BLACK",
      "Sidebar": "FOREST",
      "Topbar": "BLACK",
      "Card Surface": "OFF_BLACK",
      "Accent Band": "FOREST_DEEPER",
      "Accent": "AMBER",
      "Status / Live": "LIVE_GREEN",
      "Destructive": "RED_ERROR",
    },
    style: {
      "Thesis": "Every action audited. Nothing decorative — the room runs Coravu.",
      "Simplicity": "Dense lists, filters always visible. No editorial flourishes anywhere.",
      "Flow": "Usage-first sidebar (Dashboard / Inbox / CRM / Programs / Config). Drill down.",
      "Motion": "None. Two-click confirms on anything destructive — armed + 4s timeout.",
    },
  },
  accentTintSoft: "rgba(181,135,58,0.10)",
  neutralHoverBgOnDark: "rgba(255,255,255,0.04)",
  surfaceDividerFaint: "rgba(247,244,238,0.08)",
  surfaceDividerStrong: "rgba(247,244,238,0.18)",
  textOnSidebarFaint: "rgba(247,244,238,0.30)",
  textOnSidebarIdle: "rgba(247,244,238,0.48)",

  successTintSoft: "rgba(100,127,77,0.10)",
  successBorderSoft: "rgba(100,127,77,0.28)",
  accentBorderSoft: "rgba(181,135,58,0.30)",
  accentBorderHover: "rgba(181,135,58,0.45)",

  accentHalo: "rgba(181,135,58,0.18)",

};

/* ────────────────────────────────────────────────────────────
 * Judge — scoring portal. BLACK page bg, FOREST sidebar, and
 * FOREST_MID card surfaces (the judge queue's stat tiles and
 * assignment rows). AMBER accent, SAGE for submitted/active,
 * RED_ERROR for overdue. Focused, neutral, scoring-first voice.
 * Mirrors components/judge/JudgeShell.tsx + app/(judge)/judge.
 * ──────────────────────────────────────────────────────────── */

const JUDGE: PaneTheme = {
  paneId: "Judge",
  pageBg: COLORS.BLACK.hex,
  contentBg: COLORS.BLACK.hex,
  surfaceBg: COLORS.FOREST_MID.hex,
  surfaceRaisedBg: "#2A3D26",
  sidebarBg: COLORS.FOREST.hex,
  sidebarItemActiveBg: "rgba(181,135,58,0.10)",
  topbarBg: COLORS.BLACK.hex,
  textOnSurface: COLORS.LINEN.hex,
  textOnSurfaceDim: "rgba(247,244,238,0.66)",
  textOnSurfaceFaint: "rgba(247,244,238,0.34)",
  textOnSidebar: COLORS.LINEN.hex,
  textOnSidebarDim: "rgba(247,244,238,0.50)",
  textOnTopbar: COLORS.LINEN.hex,
  textOnTopbarDim: "rgba(247,244,238,0.55)",
  border: "rgba(255,255,255,0.07)",
  borderStrong: "rgba(181,135,58,0.40)",
  divider: "rgba(255,255,255,0.06)",
  altBg: COLORS.FOREST_DEEP.hex,
  altTextOnSurface: COLORS.LINEN.hex,
  altTextOnSurfaceDim: "rgba(247,244,238,0.62)",
  altBorder: "rgba(181,135,58,0.18)",
  altSurfaceLabel: "Dark scoring panel",
  accent: COLORS.AMBER.hex,
  accentText: COLORS.FOREST.hex,
  accentSoft: "rgba(181,135,58,0.10)",
  accentDeep: COLORS.AMBER_DEEP.hex,
  destructive: COLORS.RED_ERROR.hex,
  success: COLORS.SAGE.hex,
  hasSidebar: true,
  displayFont: FONTS.displayMedium,
  displayItalicFont: FONTS.displayItalic,
  displayMediumFont: FONTS.displayMedium,
  bodyFont: FONTS.body,
  bodyMediumFont: FONTS.bodyMedium,
  bodySemiBoldFont: FONTS.bodySemiBold,
  bodyBoldFont: FONTS.bodyBold,
  voice: { label: "Focused-Neutral" },
  cardStyle: {
    bg: COLORS.FOREST_MID.hex,
    borderColor: "rgba(255,255,255,0.07)",
    borderLeftAccent: false,
    textOnCard: COLORS.LINEN.hex,
    textDimOnCard: "rgba(247,244,238,0.66)",
    tagOnCard: COLORS.AMBER.hex,
  },
  atoms: {
    colors: {
      "Initial Background": "BLACK",
      "Sidebar": "FOREST",
      "Topbar": "BLACK",
      "Card Surface": "FOREST_MID",
      "Accent Band": "FOREST_DEEP",
      "Accent": "AMBER",
      "Status / Live": "SAGE",
      "Destructive": "RED_ERROR",
    },
    style: {
      "Thesis": "Score what's in front of you. Nothing competes with the submission under review.",
      "Simplicity": "One queue, one rubric. Each row is an assignment — open it, score it, move on.",
      "Flow": "FOREST sidebar, assignment queue first. Due-soon rises to the top; scoring is one tap away.",
      "Motion": "Calm. Rows tint on hover, scores save in place — no celebration, no overshoot.",
    },
  },
  accentTintSoft: "rgba(181,135,58,0.10)",
  neutralHoverBgOnDark: "rgba(255,255,255,0.04)",
  surfaceDividerFaint: "rgba(247,244,238,0.08)",
  surfaceDividerStrong: "rgba(247,244,238,0.18)",
  textOnSidebarFaint: "rgba(247,244,238,0.30)",
  textOnSidebarIdle: "rgba(247,244,238,0.48)",

  successTintSoft: "rgba(143,187,138,0.10)",
  successBorderSoft: "rgba(143,187,138,0.30)",
  accentBorderSoft: "rgba(181,135,58,0.30)",
  accentBorderHover: "rgba(181,135,58,0.45)",

  accentHalo: "rgba(181,135,58,0.18)",

};

export const PANE_THEMES: Record<PaneKey, PaneTheme> = {
  General: GENERAL,
  Student: STUDENT,
  Client: CLIENT,
  Admin: ADMIN,
  Judge: JUDGE,
};
